import React, { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { HiLockClosed, HiOutlineCreditCard } from "react-icons/hi";

import { UserContext } from "../../context/UserContext";
import { trialHasExpired } from "../../lib/trial";

// SCRUM-54: la prueba de 30 días venció sin conversión. Mismo bloqueo que la
// activación pendiente, pero el owner debe entender QUÉ pasó y qué toca: elegir plan.
const TrialExpiredState = ({ fallbackPath = "/my_shops" }) => (
  <section className="mx-auto flex w-full max-w-4xl flex-col gap-6 rounded-[28px] border border-amber-200 bg-white/95 p-8 shadow-sm">
    <div className="inline-flex w-fit items-center gap-2 rounded-full bg-amber-100 px-4 py-2 text-sm font-semibold uppercase tracking-[0.24em] text-amber-700">
      <HiLockClosed className="text-base" />
      Prueba finalizada
    </div>
    <div className="flex flex-col gap-3">
      <h1 className="text-3xl font-semibold text-slate-900">Tu prueba gratuita ha terminado</h1>
      <p className="max-w-2xl text-base leading-7 text-slate-600">
        Los 30 dias de prueba de esta tienda han llegado a su fin. Puedes seguir viendo el menu
        y navegar por la plataforma, pero los modulos operativos quedan bloqueados hasta elegir un plan.
      </p>
      <p className="max-w-2xl text-base leading-7 text-slate-600">
        Todo lo que configuraste durante la prueba (socios, productos, ajustes) sigue guardado:
        al activar un plan lo recuperas tal cual lo dejaste.
      </p>
    </div>
    <div className="flex flex-wrap gap-3">
      <Link
        to={`${fallbackPath}?activation=required`}
        className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
      >
        <HiOutlineCreditCard className="text-lg" />
        Elegir plan y activar la tienda
      </Link>
      <Link
        to={fallbackPath}
        className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
      >
        Volver
      </Link>
    </div>
  </section>
);

const ActivationBlockedState = ({ fallbackPath = "/my_shops" }) => (
  <section className="mx-auto flex w-full max-w-4xl flex-col gap-6 rounded-[28px] border border-amber-200 bg-white/95 p-8 shadow-sm">
    <div className="inline-flex w-fit items-center gap-2 rounded-full bg-amber-100 px-4 py-2 text-sm font-semibold uppercase tracking-[0.24em] text-amber-700">
      <HiLockClosed className="text-base" />
      Activacion pendiente
    </div>
    <div className="flex flex-col gap-3">
      <h1 className="text-3xl font-semibold text-slate-900">Esta tienda aun no esta activada</h1>
      <p className="max-w-2xl text-base leading-7 text-slate-600">
        Puedes ver el menu y navegar por la plataforma, pero los modulos operativos permanecen bloqueados hasta completar el pago de alta en Stripe.
      </p>
      <p className="max-w-2xl text-base leading-7 text-slate-600">
        En cuanto el pago quede procesado, la tienda se activara y podras usar productos, socios, contabilidad, calendario y el resto de herramientas con normalidad.
      </p>
    </div>
    <div className="flex flex-wrap gap-3">
      <Link
        to={`${fallbackPath}?activation=required`}
        className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
      >
        <HiOutlineCreditCard className="text-lg" />
        Ir a Mis Tiendas y completar el alta
      </Link>
      <Link
        to={fallbackPath}
        className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
      >
        Volver
      </Link>
    </div>
  </section>
);

const SubscriptionBlockedState = ({ fallbackPath = "/my_shops" }) => (
  <section className="mx-auto flex w-full max-w-4xl flex-col gap-6 rounded-[28px] border border-rose-200 bg-white/95 p-8 shadow-sm">
    <div className="inline-flex w-fit items-center gap-2 rounded-full bg-rose-100 px-4 py-2 text-sm font-semibold uppercase tracking-[0.24em] text-rose-700">
      <HiLockClosed className="text-base" />
      Suscripcion bloqueada
    </div>
    <div className="flex flex-col gap-3">
      <h1 className="text-3xl font-semibold text-slate-900">Esta tienda tiene la suscripcion bloqueada</h1>
      <p className="max-w-2xl text-base leading-7 text-slate-600">
        La tienda ya supero el alta inicial, pero los modulos operativos estan bloqueados porque la suscripcion mensual no esta al dia.
      </p>
      <p className="max-w-2xl text-base leading-7 text-slate-600">
        En cuanto reanudes el cobro en Stripe, recuperara el acceso normal a productos, socios, contabilidad, calendario y el resto de herramientas.
      </p>
    </div>
    <div className="flex flex-wrap gap-3">
      <Link
        to={`${fallbackPath}?subscription=required`}
        className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
      >
        <HiOutlineCreditCard className="text-lg" />
        Ir a Mis Tiendas y reactivar la suscripcion
      </Link>
      <Link
        to={fallbackPath}
        className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
      >
        Volver
      </Link>
    </div>
  </section>
);

// Staff cannot pay: they have no /my_shops and no card on file. Sending them to the
// checkout would be a dead end, so they get told who *can* fix it instead.
const StaffBlockedState = ({ subscriptionLapsed = false, trialExpired = false }) => (
  <section className="mx-auto flex w-full max-w-4xl flex-col gap-6 rounded-[28px] border border-amber-200 bg-white/95 p-8 shadow-sm">
    <div className="inline-flex w-fit items-center gap-2 rounded-full bg-amber-100 px-4 py-2 text-sm font-semibold uppercase tracking-[0.24em] text-amber-700">
      <HiLockClosed className="text-base" />
      Tienda bloqueada
    </div>
    <div className="flex flex-col gap-3">
      <h1 className="text-3xl font-semibold text-slate-900">Esta tienda no esta activa</h1>
      <p className="max-w-2xl text-base leading-7 text-slate-600">
        {trialExpired
          ? "La prueba gratuita del club ha terminado, asi que los modulos operativos estan bloqueados."
          : subscriptionLapsed
          ? "La suscripcion mensual de la tienda no esta al dia, asi que los modulos operativos estan bloqueados."
          : "La tienda todavia no ha completado el alta, asi que los modulos operativos estan bloqueados."}
      </p>
      <p className="max-w-2xl text-base leading-7 text-slate-600">
        <strong className="font-semibold text-slate-900">Avisa al responsable del club.</strong>{" "}
        Solo la persona propietaria puede regularizar el pago desde su cuenta. En cuanto lo haga,
        recuperaras el acceso con normalidad.
      </p>
    </div>
  </section>
);

const VenueActivationRoute = ({ fallbackPath = "/my_shops", children }) => {
  // Consumo posicional: [7] accessPrivileges, [12] activation, [13] subscription,
  // [15] trial_ends_at (SCRUM-54; el [14] es canManageProducts).
  const [, , , , , , , accessPrivileges, , , , , venueActivationStatus, venueSubscriptionStatus, , venueTrialEndsAt] = useContext(UserContext);
  const location = useLocation();

  // Superadmin runs the platform and must reach every club, paid or not.
  const isSuperadmin = accessPrivileges === "superadmin";
  const isOwner = accessPrivileges === "owner";
  const isActiveVenue = (venueActivationStatus || "pending") === "active";
  const blockedSubscriptionStatuses = new Set(["past_due", "unpaid", "canceled", "incomplete_expired"]);
  const isSubscriptionBlocked =
    isActiveVenue && blockedSubscriptionStatuses.has((venueSubscriptionStatus || "").toLowerCase());

  if (isSuperadmin) return children;

  const isBlocked = !isActiveVenue || isSubscriptionBlocked;
  if (!isBlocked) return children;

  // The owner can fix this: send them to billing. Anyone else (staff) cannot — they have
  // no /my_shops and no card on file — so pointing them at the checkout is a dead end.
  // Until now the guard only looked at owners, which meant a club that had not paid could
  // still be run in full by its staff.
  // SCRUM-54: si el bloqueo es por activación y hubo una prueba que venció, la pantalla
  // explica eso — no el genérico. Con suscripción impagada manda la suscripción (la
  // prueba antigua es historia), igual que en core/venue_status.py del backend.
  const trialExpired = !isActiveVenue && trialHasExpired(venueTrialEndsAt);

  if (!isOwner) {
    return <StaffBlockedState subscriptionLapsed={isSubscriptionBlocked} trialExpired={trialExpired} />;
  }

  if (location.pathname === fallbackPath) return children;

  return isSubscriptionBlocked
    ? <SubscriptionBlockedState fallbackPath={fallbackPath} />
    : trialExpired
    ? <TrialExpiredState fallbackPath={fallbackPath} />
    : <ActivationBlockedState fallbackPath={fallbackPath} />;
};

export default VenueActivationRoute;
